import { useForm } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { InputField, SelectField, TextareaField, PhoneInputField } from "@/components/form-field"
import { Plus, Trash } from "lucide-react"
import { Link } from "@inertiajs/react"
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { set } from 'lodash'

interface AwardContact {
  id?: number
  first_name: string
  last_name: string
  phone_office?: string
  phone_mobile?: string
  phone_other?: string
  address_1?: string
  address_2?: string
  city?: string
  state?: string
  postal_code?: string
  email?: string
  notes?: string
  roles?: string[]
}

interface Award {
  id?: number
  supplier_id: number
  award_contacts_attributes: AwardContact[]
}

interface Props {
  deal: {
    id: number
    name: string
  }
  award?: Award
  suppliers: Array<{
    id: number
    name: string
  }>
  errors?: Record<string, string>
}

const formatPhoneNumber = (phoneNumber: string | undefined): string => {
  if (!phoneNumber) return '';

  try {
    const parsed = parsePhoneNumberFromString(phoneNumber, 'US')
    if (parsed) {
      return parsed.formatInternational()
    }
  } catch (e) {
    return phoneNumber
  }

  return phoneNumber
}

export default function Form({ deal, award, suppliers, errors = {} }: Props) {
  // Format phone numbers in initial data
  const initialContacts = award?.award_contacts?.map(contact => ({
    ...contact,
    phone_office: formatPhoneNumber(contact.phone_office),
    phone_mobile: formatPhoneNumber(contact.phone_mobile),
    phone_other: formatPhoneNumber(contact.phone_other)
  })) || []

  const { data, setData, post, put, processing } = useForm<Award>({
    supplier_id: award?.supplier_id || suppliers[0]?.id || 0,
    award_contacts_attributes: initialContacts
  })

  const handleSelectChange = (name: string, value: string) => {
    setData(name as keyof Award, value)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (award?.id) {
      put(`/deals/${deal.id}/awards/${award.id}`)
    } else {
      post(`/deals/${deal.id}/awards`)
    }
  }

  const addContact = () => {
    setData('award_contacts_attributes', [
      ...data.award_contacts_attributes,
      {
        first_name: '',
        last_name: ''
      }
    ])
  }

  const removeContact = (index: number) => {
    setData('award_contacts_attributes',
      data.award_contacts_attributes.filter((_, i) => i !== index)
    )
  }

  // NOTE: only used by nested award_contact fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    // Clone the current data
    const updatedData = { ...data }
    // Use lodash set to update the nested value
    set(updatedData, name, value)
    setData(updatedData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">

      <Card>
        <CardContent>
          <SelectField
            name="supplier_id"
            label="Supplier"
            data={data}
            errors={errors}
          options={suppliers.map(s => ({ value: s.id, label: s.name }))}
            onValueChange={handleSelectChange}
          />
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className={`flex items-center justify-between ${data.award_contacts_attributes.length >= 1 ? '' : 'border-b border-gray-100 pb-2'}`}>
          <h3 className="text-lg font-medium">Contacts</h3>
        </div>

        {data.award_contacts_attributes.length === 0 && (
          <p className="text-muted-foreground">No contacts added yet. Click "Add Contact" to get started.</p>
        )}

        {data.award_contacts_attributes.map((contact, index) => (
          <Card key={index} className="py-0">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium">Contact {index + 1}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeContact(index)}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Contact Info */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      name={`award_contacts_attributes.${index}.first_name`}
                      label="First Name"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                    />
                    <InputField
                      name={`award_contacts_attributes.${index}.last_name`}
                      label="Last Name"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                    />
                  </div>
                  <InputField
                    name={`award_contacts_attributes.${index}.email`}
                    label="Email"
                    type="email"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                  <PhoneInputField
                    name={`award_contacts_attributes.${index}.phone_office`}
                    label="Phone (Office)"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                  <PhoneInputField
                    name={`award_contacts_attributes.${index}.phone_mobile`}
                    label="Phone (Mobile)"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                  <PhoneInputField
                    name={`award_contacts_attributes.${index}.phone_other`}
                    label="Phone (Other)"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Right Column - Address Info */}
                <div className="space-y-4">
                  <InputField
                    name={`award_contacts_attributes.${index}.address_1`}
                    label="Address 1"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                  <InputField
                    name={`award_contacts_attributes.${index}.address_2`}
                    label="Address 2"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6">
                      <InputField
                        name={`award_contacts_attributes.${index}.city`}
                        label="City"
                        data={data}
                        errors={errors}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-span-3">
                      <InputField
                        name={`award_contacts_attributes.${index}.state`}
                        label="State"
                        data={data}
                        errors={errors}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-span-3">
                      <InputField
                        name={`award_contacts_attributes.${index}.postal_code`}
                        label="Postal Code"
                        data={data}
                        errors={errors}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <TextareaField
                    name={`award_contacts_attributes.${index}.notes`}
                    label="Notes"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-between">
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={addContact}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
        <div className="space-x-4">
          <Link href={`/deals/${deal.id}`}>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={processing}>
            {award?.id ? 'Update Award' : 'Create Award'}
          </Button>
        </div>
      </div>
    </form>
  )
}
